import {html} from 'lit-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {classMap} from 'lit-html/directives/class-map';

// LAZY
//import Choices from 'choices.js';
//import 'choices.js/public/assets/styles/choices.css';

import TemplateRegistry from '../template/template-registry';
import ResourceLoader from '../resource-loader';
import Translate from '../translate';
import { ThemeCurrency, PriceType } from '../const';
import { cdnImg } from '../shopify-cdn';
import {
    optionValueText, getOptionValue,
    valueSync, optionClassMap, optionName,
    optionValuesGroupBy
} from '../template/template-util';

class DropdownController {
    constructor(onChange) {
        this.onChange = onChange;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render
        this.ready = null;
        this.instance = null;
        this.optionEl = null;
        this.selectEl = null;
        this.multiSelect = false;
        this._empty = false;
    }

    setup() {
        // resources loaded async
        let resources = [
            this.config.assets + '/static/lib/choicesjs/choices.min.css',
            this.config.assets + '/static/lib/choicesjs/choices.min.js'
        ];

        // load resources if not already
        this.ready = ResourceLoader.loadOnce(resources, 'option-dropdown',  {async: false /* fetch parallel - load in series */ });
    }

    mapValues(option) {

        /*  INFO: the lib supports parsing the initial options, but then replaces the <option> content with the single selection, which then is not updated by lit-html (use case editor mode) we use programmatic choice mapping instead

            ${!option.value ? html`<option value="" selected></option>` : null}

            ${repeat(option.values || [],  (optionValue,index) => html`
                <option .value=${optionValue.value} ?selected=${option.value == optionValue.value} data-custom-properties=${ifDefined(optionValue.img)}> ${optionValueText(option, optionValue, ThemeCurrency.moneyFormat)}</option>
            `)}
         */

        const mapChoice = (optionValue) => {
            let selected = option.value == optionValue.value;

            // multi select support (not used for restore - but if price formula updates the text mapValues is called)
            if(!selected && option.value && this.multiSelect){
                selected = this.isSelected(option, optionValue);
            }

            return {
                value: optionValue.value,
                label: optionValueText(option, optionValue, ThemeCurrency.moneyFormat),
                customProperties: optionValue.img, // same as data-custom-properties
                selected: selected,
                disabled: optionValue.disabled === true  // INFO:  variant unvailalbe currently not supported
            }
        };

        let values = [];

        // groupDelimiter support: use Choices.js native group format
        if(option.params.groupDelimiter && option.values){
            let groupValues = optionValuesGroupBy(option.values, option.params.groupDelimiter);

            for(let i = 0; i < groupValues.length; i++){
                let group = groupValues[i];
                if(group.groupLabel){
                    // Choices.js group format: { label, choices: [] }
                    values.push({
                        label: group.groupLabel,
                        choices: group.values.map(mapChoice)
                    });
                } else {
                    // ungrouped value
                    values.push(mapChoice(group));
                }
            }
        } else {
            // IMPORTANT: check if values are provided
            values = option.values ? option.values.map(mapChoice) : [];
        }

        // empty selection
        if(!option.value && !option.params.multiSelect){
            values.unshift({ value: '', label: option.params.textValueSelect || '', selected: true});
            // flag to remove after value is set
            this._empty = true;
        } else if(this._empty){ // remove flag
            delete this._empty
        }

        return values;
    }

    handleChangeMulti(e, option) {
        // choices api to return selected values only
        let values = this.instance.getValue(true);
        let value = values.join(',');
        // update hidden input value
        this.optionEl.value = value;
        // sync with option model
        option.value = value;

        this.onChange(option);
    }

    // Multi-select methods - copied from controllerMultiSelect
    isSelected(option, optionValue) {

        // equal check same as in product-options.js
        if (String(optionValue.value).trim() === String(option.value).trim()) {
            return true;
        }
        // value contains check
        if (option.value && option.value.indexOf(optionValue.value) !== -1) {
            // NEW: we also have to split to check for the exact value match
            let values = option.value.split(',');
            if (values.indexOf(optionValue.value) !== -1) {
                return true;
            }
        }
        return false;
    }

    onValidate(option) {

        if (option.required && !option.value) {
            option.errors.push(Translate.get('product.options.validity.valueMissing'));
            return false;
        }

        if(option.params.multiSelect) {
            // Multi-select validation
            let count = option.value ? option.value.split(',').length : 0;

            if (option.params.minSelect && count < option.params.minSelect) {
                option.errors.push(Translate.get('product.options.validity.selectMin', {min: option.params.minSelect}));
                return false;
            }
            if (option.params.maxSelect && count > option.params.maxSelect) {
                option.errors.push(Translate.get('product.options.validity.selectMax', {max: option.params.maxSelect}));
                return false;
            }
        }

        return true;
    }

    init(option) {

        // wait for resources to be ready before init
        this.ready.then(() => {

            // select element (choices lib only stores the selected value)
            let optionEl = document.querySelector('#' + option.id);

            // NEW: multi select has different select element (main option value is a hidden input)
            if(option.params.multiSelect){
                this.optionEl = optionEl;
                this.selectEl = document.querySelector('#' + option.id + '-select');

                // Set multiSelect flag
                this.multiSelect = true;
            } else {
                this.selectEl = optionEl;
            }

            const valueTemplate = (data) => {
                let content = `<span class="o--text">${data.label}</span>`;

                // img set via data-custom-properties in template
                if(data.customProperties){
                    return `<img src="${cdnImg(data.customProperties, '200x')}" class="o--img"/> ${content}`;
                }

                return content;
            }

            this.instance = new Choices(this.selectEl, {
                choices: this.mapValues(option),
                itemSelectText: '', // avoid showing non translated text
                noResultsText: '',
                noChoicesText: '',
                removeItemButton: option.params.multiSelect ? true : false,
                maxItemCount: option.params.maxSelect ? option.params.maxSelect : -1,
                maxItemText: option.params.maxSelect ? Translate.get('product.options.validity.selectMax', { max: option.params.maxSelect }) : '',
                searchPlaceholderValue: option.params.textSearchValues || '',
                searchEnabled: option.params.searchValues || false,
                searchChoices: option.params.searchValues || false,
                position: option.params.dropdownPosition || 'auto',
                fuseOptions: window.clDropdownFuseOptions || {}, // optional fuse.js options for exact search use window.clDropdownFuseOptions = { threshold: 0.0 };
                // searchResultLimit: 4, // default
                //searchFields: ['label', 'value'], // default search label and value
                shouldSort: false,
                callbackOnCreateTemplates: function(template) {
                    return {
                        item: (classNames, data, selectText) => {

                            /* DEFAULT TEMPLATE CONTENT:
                            let itemEl = template(`
                              <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable} ${data.placeholder ? classNames.placeholder : ''}"
                               data-item data-id="${data.id}" data-value="${data.value}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''}>
                                ${data.label}
                              </div>
                            `); */

                            // INFO: we call the default template and just modify the innerHTML
                            let itemEl = Choices.defaults.templates.item.apply(this, [classNames, data, selectText]);

                            let valueHTML = valueTemplate(data);

                            // NEW: multi select has custom html for remove button - only replace label and keep the rest
                            if(option.params.multiSelect){
                                itemEl.innerHTML = itemEl.innerHTML.replace(data.label, valueHTML);
                                itemEl.lastElementChild.innerHTML = 'X'; // replace remove button content with X (we apply custom css to this)
                            } else {
                                itemEl.innerHTML = valueHTML;
                            }
                            // NEW: empty custom placeholder class (only used for styling)
                            if(data.value === ''){
                                itemEl.classList.add('cl-po--placeholder');
                            }

                            return itemEl;
                        },
                        choice: (classNames, data, selectText) => {

                            /* DEFAULT TEMPLATE CONTENT:
                              let choiceEl = template(`
                                  <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}"
                                   data-select-text="${this.config.itemSelectText}" data-choice ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
                                   data-id="${data.id}" data-value="${data.value}" ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}>
                                    ${data.label}
                                  </div>
                                `);
                             */

                            // INFO: we call the default template and just modify the innerHTML
                            let choiceEl = Choices.defaults.templates.choice.apply(this, [classNames, data, selectText]);

                            choiceEl.innerHTML = valueTemplate(data);

                            // NEW: add disabled class (can be used to hide disabled options)
                            if(data.disabled){
                                choiceEl.classList.add('cl-po--disabled');
                            }
                            // NEW: empty custom placeholder class (only used for styling)
                            if(data.value === ''){
                                choiceEl.classList.add('cl-po--placeholder');
                            }

                            return choiceEl;
                        },
                    };
                },
            });
        });

    }

    updateChoices(option) {
        // update values
        if(this.instance){
            // editor mode update - reset value (is set again via value.selected)
            if(option.value && option.params.multiSelect){
                this.instance.clearStore();
            }
            this.instance.setChoices(this.mapValues(option), 'value', 'label', true);
        }
    }

    update(option) {
        // update value if changed from outside
        if(this.instance){

            // NEW: multi select restore value from hidden input
            if(option.params.multiSelect){
                if(this.optionEl && option.value !== this.optionEl.value) {
                    this.instance.setChoiceByValue(option.value.split(','));
                }
            } else {
                if(this.selectEl && option.value !== this.selectEl.value){
                    this.instance.setChoiceByValue(option.value);
                }
            }
        }
    }
}

TemplateRegistry.templates['dropdown'] = (option, config, onChange) => {
    // init flag when no controller yet
    let init = option._controller === undefined;
    
    // get or create dropdown controller
    option._controller = option._controller || new DropdownController(onChange);
    option._controller.config = config;
    option._controller.option = option;

    // setup dropdown instance
    if(config.productForm){
        if(init){

            // load resources
            option._controller.setup();

            const initController = () => {
                // remove init event
                config.productForm.removeEventListener('options:rendered', initController);
                // call init
                option._controller.init(option);
            };
            // wait for options render to complete ( could be solved better by support custom javascript post render execution by option ( maybe define on _controller.postRender() )
            config.productForm.addEventListener('options:rendered', initController);
        } else {
            // INFO: editor mode always update the values  (if used in the future with other dependent values we have to call this dynamically if the values changed)
            if(config.editorMode){
                // multi select toggle on/off requires full init
                if(option._controller.multiSelect !== (option.params.multiSelect === true)){
                    // editor mode toggle multiSelect remove flag
                    delete option._controller.multiSelect;
                    // call init to update multiSelect change
                    option._controller.init(option);
                } else {
                    option._controller.updateChoices(option);
                }

            } else {
                // item restore check
                option._controller.update(option);

                // hacky: priceFormula|percentage requires re mapping of choices to show the updated price
                if(option.params.valuePriceText){
                    let optionValue = getOptionValue(option);
                    if(optionValue && (optionValue._priceFunction || optionValue.priceType === PriceType.PERCENTAGE)){
                        option._controller.updateChoices(option);
                    }
                }
                // hacky: variant option values can also be dynamically filtered - requires re-mapping
                if(option._variantOption){
                    option._controller.updateChoices(option);
                }

                // NEW: remove empty choice if now a value is set NOTE: if we always would map values this would not be needed
                if(option.value && option._controller._empty){
                    option._controller.updateChoices(option);
                    delete option._controller._empty;
                }
            }
        }
    }

    if(option.params.multiSelect){
        return html`
        <input type="hidden" name="${optionName(option)}" id="${option.id}" value=${option.value} form=${ifDefined(config.productFormLink)} />
        <select class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-select" @change=${(e) => option._controller.handleChangeMulti(e, option)} multiple>
        </select>
        `;
    } else {
        return html`
        <select class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}" name="${optionName(option)}" @change=${(e) => valueSync(e, option, onChange)} ?required=${option.required} form=${ifDefined(config.productFormLink)}>
        </select>
        `;
    }
}